import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Button, CircularProgress,
  Stepper, Step, StepLabel, Paper, Divider, TextField, FormControl,
  Select, MenuItem, IconButton, RadioGroup, FormControlLabel, Radio,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Icon } from "../../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../../constants/color";
import { useAuth } from "../../../context/authContext";
import AppToast from "../../../components/AppToast";
import axios from "axios";
import { url } from "../../../constants/url";
import { useNavigate } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

const STEPS = ["Request Details", "Review & Submit"];

const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_TTt6Om8EcjjDvw";

const CreateRequestWizard = () => {
  const navigate = useNavigate();
  const { token, refreshUserData, userData } = useAuth();

  const topRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [activeStep, setActiveStep] = useState(0);
  const [pdfPreviewModal, setPdfPreviewModal] = useState({ open: false, url: null, title: "" });

  const handlePreviewPdf = (file, label) => {
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    setPdfPreviewModal({
      open: true,
      url: blobUrl,
      title: `${label}: ${file.name}`
    });
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewModal({ open: false, url: null, title: "" });
  };

  // Step 1
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [invoiceSentEmail, setInvoiceSentEmail] = useState(null);

  // Step 2 (The 7-section form)
  const [formData, setFormData] = useState({
    propertyType: "",
    propertyName: "",
    buildingName: "",
    wing: "",
    floor: "",
    unitNumber: "",
    sqft: "",
    fullAddress: "",
    city: "Pune",
    state: "Maharashtra",
    pincode: "",
    fullName: userData?.name || "",
    mobileNumber: userData?.phone || "",
    emailAddress: userData?.email || "",
    altContactNumber: "",
    inspectionDateFrom: null,
    inspectionDateTo: null,
    preferredTimeFrom: null,
    preferredTimeTo: null,
    preferredTimeSlot: null,
    propertyStatus: "",
    purposeOfInspection: "",
    builderName: "",
    projectName: "",
    towerPhase: "",
    reraNumber: "",
    contactPersonName: "",
    contactPersonMobile: "",
    accessAvailable: "false",
    securityGatePass: "false",
    accessInstructions: "",
    landmark: "",
    specialInstructions: "",
    areasOfConcern: "",
    floorPlan: null,
    agreementCopy: null,
    builderLayout: null,
    previousReport: null,
  });

  const [openFromDate, setOpenFromDate] = useState(false);
  const [openToDate, setOpenToDate] = useState(false);
  const [openTimeFrom, setOpenTimeFrom] = useState(false);
  const [openTimeTo, setOpenTimeTo] = useState(false);

  useEffect(() => {
    if (userData) {
      setFormData(prev => ({
        ...prev,
        fullName: prev.fullName || userData.name || "",
        mobileNumber: prev.mobileNumber || userData.phone || "",
        emailAddress: prev.emailAddress || userData.email || "",
      }));
    }
  }, [userData]);



  useEffect(() => {
    if (formData.preferredTimeFrom && formData.preferredTimeTo) {
      try {
        const fromStr = format(formData.preferredTimeFrom, "hh:mm a");
        const toStr = format(formData.preferredTimeTo, "hh:mm a");
        setFormData(prev => ({
          ...prev,
          preferredTimeSlot: `${fromStr} - ${toStr}`
        }));
      } catch (e) {
        console.error("Error formatting time:", e);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        preferredTimeSlot: null
      }));
    }
  }, [formData.preferredTimeFrom, formData.preferredTimeTo]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const showToast = (type, title, message) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const sortPlans = (plansList) => {
    const sortOrder = [
      "1 BHK",
      "2 BHK",
      "2.5 BHK",
      "3 BHK",
      "4 BHK",
      "5 BHK",
      "Studio",
      "Bungalow",
      "Shop / Office (Up to 1000 sq ft)",
      "Shop / Office (1000-5000 sq ft)",
    ];
    return [...plansList].sort((a, b) => {
      const idxA = sortOrder.indexOf(a.propertyType);
      const idxB = sortOrder.indexOf(b.propertyType);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.propertyType.localeCompare(b.propertyType);
    });
  };

  useEffect(() => {
    const loadPlans = async () => {
      setFetchingPlans(true);
      try {
        const response = await axios.get(`${url}/api/client/fir-credits`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) setPlans(sortPlans(response.data.data || []));
      } catch (error) {
        console.error("Fetch FIR plans error:", error);
      } finally {
        setFetchingPlans(false);
      }
    };
    if (token) loadPlans();
  }, [token]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) return resolve(true);
      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePurchase = async (plan) => {
    setLoadingPlan(plan._id);
    try {
      const basePrice = plan.price;
      const gstAmount = Math.round(basePrice * 0.18);
      const totalAmount = Math.round(basePrice + gstAmount);

      const orderRes = await axios.post(
        `${url}/api/payment/create-order`,
        { amount: totalAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const order = orderRes.data?.data;
      if (!order?.id) {
        showToast("error", "Payment Failed", "Failed to create payment order.");
        return setLoadingPlan(null);
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        showToast("error", "Payment Error", "Could not load Razorpay. Check your internet connection.");
        return setLoadingPlan(null);
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "Arconest",
        description: `Purchase: ${plan.propertyType} (incl. 18% GST)`,
        order_id: order.id,
        handler: (response) => {
          console.log("[Razorpay Payment Handler] Payment received from gateway:", response);
          console.log("[Razorpay Payment Handler] Current user token:", token);
          console.log("[Razorpay Payment Handler] User Details:", userData);
          console.log("[Razorpay Payment Handler] Purchased Plan Details:", plan);

          // 1. Instantly transition to the form filling step (Step 2)
          setActiveStep(1);
          setFormData(prev => ({ ...prev, propertyType: plan.propertyType }));
          setSelectedPlan(plan);
          setLoadingPlan(null);
          topRef.current?.scrollIntoView({ behavior: 'smooth' });

          console.log("[Razorpay Payment Handler] Sending request to verify payment at:", `${url}/api/payment/verify`);
          // 2. Perform verification and provisioning in the background
          axios.post(
            `${url}/api/payment/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
            .then(verifyRes => {
              console.log("[Razorpay Payment Handler] Payment verify response:", verifyRes.data);
              if (verifyRes.data?.success) {
                console.log("[Razorpay Payment Handler] Verification success! Now calling mock-fir-payment...");
                return axios.post(
                  `${url}/api/client/mock-fir-payment`,
                  { planId: plan._id, planName: plan.propertyType, amount: totalAmount },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              }
              throw new Error("Verification failed: " + (verifyRes.data?.message || "unknown reason"));
            })
            .then(provisionRes => {
              console.log("[Razorpay Payment Handler] mock-fir-payment provisioning response:", provisionRes.data);
              if (provisionRes.data?.success) {
                console.log("[Razorpay Payment Handler] Provisioning successful! Plan added to DB.");
                showToast("success", "Payment Verified", `Your FIR plan has been activated successfully.`);
                refreshUserData();
                setInvoiceSentEmail({
                  email: provisionRes.data.clientEmail || userData?.email,
                  purchaseId: provisionRes.data.purchaseId,
                  emailSent: provisionRes.data.emailSent
                });
              } else {
                console.error("[Razorpay Payment Handler] Provisioning failed:", provisionRes.data.message);
                showToast("error", "Provisioning Failed", provisionRes.data.message || "Could not allocate credits. Please contact support.");
              }
            })
            .catch(err => {
              console.error("[Razorpay Payment Handler] Background payment provisioning error caught:", err);
              if (err.response) {
                console.error("[Razorpay Payment Handler] Error Response status:", err.response.status);
                console.error("[Razorpay Payment Handler] Error Response data:", err.response.data);
              }
              showToast("error", "Payment Verification Issue", "We are verifying your transaction in the background. If your credits do not update, please contact support.");
            });
        },
        prefill: {
          name: userData?.name || "",
          email: userData?.email || "",
          contact: userData?.phone || "",
        },
        theme: { color: "#421A6E" },
        modal: {
          ondismiss: () => {
            setLoadingPlan(null);
            showToast("info", "Payment Cancelled", "You cancelled the payment.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      showToast("error", "Payment Failed", "Could not complete the purchase.");
      setLoadingPlan(null);
    }
  };

  const handleChange = (field, value) => {
    if (["pincode", "mobileNumber", "altContactNumber", "contactPersonMobile"].includes(field)) {
      const numericValue = value.replace(/\D/g, "");
      if (field === "pincode" && numericValue.length > 6) return;
      if (field !== "pincode" && numericValue.length > 10) return;
      setFormData((prev) => ({ ...prev, [field]: numericValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.propertyType || !formData.fullAddress || !formData.state || !formData.pincode ||
      !formData.fullName || !formData.mobileNumber || !formData.emailAddress ||
      !formData.inspectionDateFrom || !formData.inspectionDateTo || !formData.preferredTimeSlot ||
      !formData.propertyStatus || !formData.purposeOfInspection) {
      showToast("error", "Validation Error", "Please fill all required fields (*)");
      return false;
    }

    if (!/^\d{6}$/.test(formData.pincode)) {
      showToast("error", "Validation Error", "Please enter a valid 6-digit PIN Code");
      return false;
    }

    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      showToast("error", "Validation Error", "Please enter a valid 10-digit Mobile Number");
      return false;
    }

    if (formData.altContactNumber && !/^\d{10}$/.test(formData.altContactNumber)) {
      showToast("error", "Validation Error", "Please enter a valid 10-digit Alternate Mobile Number");
      return false;
    }

    if (formData.contactPersonMobile && !/^\d{10}$/.test(formData.contactPersonMobile)) {
      showToast("error", "Validation Error", "Please enter a valid 10-digit Contact Person Mobile");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      showToast("error", "Validation Error", "Please enter a valid Email Address");
      return false;
    }

    return true;
  };

  const handleSingleFileChange = (field, file) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = new FormData();
      const finalContactPersonName = formData.contactPersonName || formData.fullName || "";
      const finalContactPersonMobile = formData.contactPersonMobile || formData.mobileNumber || "";

      Object.keys(formData).forEach(key => {
        if (['floorPlan', 'agreementCopy', 'builderLayout', 'previousReport'].includes(key)) {
          if (formData[key]) {
            submitData.append(key, formData[key]);
          }
        } else if (key === 'inspectionDateFrom' || key === 'inspectionDateTo') {
          submitData.append(key, formData[key] ? formData[key].toISOString() : '');
        } else if (key === 'contactPersonName') {
          submitData.append(key, finalContactPersonName);
        } else if (key === 'contactPersonMobile') {
          submitData.append(key, finalContactPersonMobile);
        } else {
          submitData.append(key, formData[key]);
        }
      });

      const response = await axios.post(`${url}/api/client/create-fir-project`, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        showToast("success", "Success", "FIR Request created successfully!");
        refreshUserData();
        setTimeout(() => navigate("/projects/all"), 1500);
      }
    } catch (error) {
      showToast("error", "Error", error.response?.data?.message || "Failed to submit request.");
    }
  };

  /* ---------------- Input Style ---------------- */
  const inputStyle = {
    width: "100%",
    maxWidth: "321px",
    "&.MuiFormControl-root": {
      maxWidth: "321px !important"
    },
    "&.MuiTextField-root": {
      maxWidth: "321px !important"
    },

    "& .MuiOutlinedInput-root": {
      height: "50px",
      borderRadius: "7px",

      "& fieldset": {
        borderWidth: "1px",
        borderColor: COLORS.GREY_COLOR
      },

      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY
      },

      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY
      },
      "& input": {
        color: COLORS.PRIMARY,
        fontFamily: FONT_FAMILY
      }
    },

    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
      height: "50px",
      boxSizing: "border-box",
      color: COLORS.PRIMARY,
      fontFamily: FONT_FAMILY
    }
  };

  const datePickerInputStyle = {
    ...inputStyle,
    width: "321px !important",
    minWidth: "321px !important",
    maxWidth: "321px !important",
    display: "flex !important",
    "&.MuiFormControl-root": {
      width: "321px !important",
      minWidth: "321px !important",
      maxWidth: "321px !important",
      display: "flex !important"
    },
    "&.MuiTextField-root": {
      width: "321px !important",
      minWidth: "321px !important",
      maxWidth: "321px !important",
      display: "flex !important"
    },

    "& .MuiOutlinedInput-root": {
      width: "321px !important",
      minWidth: "321px !important",
      maxWidth: "321px !important",
      height: "50px !important",
      minHeight: "50px !important",
      borderRadius: "7px !important",
      boxSizing: "border-box !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "space-between !important",
      "& fieldset": {
        borderWidth: "1px !important",
        borderColor: COLORS.GREY_COLOR
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY
      },
      "& input": {
        width: "100% !important",
        height: "50px !important",
        padding: "0 14px !important",
        boxSizing: "border-box !important",
        color: COLORS.PRIMARY,
        fontFamily: FONT_FAMILY,
        fontSize: "14px"
      },
      "& .MuiInputAdornment-root": {
        height: "100%",
        maxHeight: "50px",
        "& .MuiIconButton-root": {
          color: COLORS.PRIMARY
        }
      }
    }
  };

  const autoGrowStyle = {
    ...inputStyle,
    "& .MuiOutlinedInput-root": {
      ...inputStyle["& .MuiOutlinedInput-root"],
      height: "auto",
      minHeight: "50px",
      padding: "13px 14px",
      alignItems: "center",
      "& textarea": {
        padding: "0",
        color: COLORS.PRIMARY,
        fontFamily: FONT_FAMILY
      }
    }
  };

  const labelStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    mb: 1,
    display: "block"
  };

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 200,
        overflowY: "auto",
        marginTop: "4px"
      }
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left"
    }
  };

  const rowContainerStyle = {
    display: "grid",
    gridTemplateColumns: {
      xs: "100%",
      sm: "repeat(2, 1fr)",
      md: "repeat(3, 1fr)"
    },
    gap: "24px",
    mb: "28px",
    width: "100%"
  };

  const columnContainerStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0
  };

  const sectionPaperStyle = {
    p: { xs: 2, md: 4 },
    mb: 4,
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
    backgroundColor: "#fff",
    border: `1px solid ${COLORS.LIGHT_SECONDARY}40`
  };

  const sectionHeaderStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    fontWeight: 700,
    mb: 3,
  };

  const FileUploadBox = ({ label, file, onFileSelect }) => {
    const inputRef = React.useRef(null);

    return (
      <Box sx={columnContainerStyle}>
        <Typography sx={labelStyle}>{label}</Typography>
        <input
          type="file"
          ref={inputRef}
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              onFileSelect(e.target.files[0]);
            }
          }}
        />
        <Box
          onClick={() => inputRef.current?.click()}
          sx={{
            ...inputStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "50px",
            borderRadius: "7px",
            border: `1px solid ${COLORS.GREY_COLOR}`,
            px: 2,
            boxSizing: "border-box",
            cursor: "pointer",
            backgroundColor: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: COLORS.SECONDARY
            }
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: "14px",
              color: file ? COLORS.PRIMARY : COLORS.LIGHT_SECONDARY,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              pr: 1
            }}
          >
            {file ? file.name : "Choose PDF file..."}
          </Typography>
          <Button
            size="small"
            component="span"
            sx={{
              minWidth: "auto",
              px: 1.8,
              py: 0.6,
              backgroundColor: file ? COLORS.PRIMARY : "#F0F4F8",
              color: file ? "#FFFFFF" : COLORS.PRIMARY,
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "6px",
              flexShrink: 0,
              boxShadow: file ? "0 2px 8px rgba(66, 26, 110, 0.25)" : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: file ? COLORS.SECONDARY : COLORS.PRIMARY,
                color: "#FFFFFF"
              }
            }}
          >
            {file ? "Change" : "Browse"}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStep1 = () => (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 1 }}>Select Your Plan</Typography>
      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 4 }}>
        Choose a property plan to proceed with creating your Field Inspection Request.
      </Typography>

      {fetchingPlans ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(3, 1fr)"
          },
          gap: 3,
          width: "100%"
        }}>
          {plans.map((plan) => {
            const hasUnconsumedPlan = userData?.purchasedFirPlans?.some(
              (p) => p.propertyType === plan.propertyType && p.isConsumed !== true
            );

            const basePrice = plan.price;
            const gstAmount = Math.round(basePrice * 0.18);
            const totalPrice = Math.round(basePrice + gstAmount);

            return (
              <Card key={plan._id} sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 8px 25px rgba(0,0,0,0.1)",
                }
              }}>
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 3 }}>
                  <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 1, fontWeight: 700 }}>{plan.propertyType}</Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.TEXT, mb: 3, flexGrow: 1, lineHeight: 1.5 }}>
                    {plan.description}
                  </Typography>

                  {/* Price & 18% GST Breakdown Box */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: COLORS.LIGHT_SECONDARY, borderRadius: 2, textAlign: "center" }}>
                    <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, fontWeight: 800 }}>
                      ₹{basePrice.toLocaleString("en-IN")}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: COLORS.PRIMARY, fontWeight: 600, mt: 0.5 }}>
                      + 18% GST (₹{gstAmount.toLocaleString("en-IN")}) = Total: ₹{totalPrice.toLocaleString("en-IN")}
                    </Typography>
                  </Box>

                  {hasUnconsumedPlan ? (
                    <Button
                      variant="contained"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setFormData(prev => ({ ...prev, propertyType: plan.propertyType }));
                        setActiveStep(1);
                        topRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      sx={{
                        backgroundColor: COLORS.SECONDARY,
                        borderRadius: 2,
                        py: 1.2,
                        fontWeight: 600,
                        "&:hover": { backgroundColor: COLORS.PRIMARY }
                      }}
                    >
                      Continue Request
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={loadingPlan === plan._id}
                      onClick={() => handlePurchase(plan)}
                      sx={{
                        backgroundColor: COLORS.PRIMARY,
                        borderRadius: 2,
                        py: 1.2,
                        fontWeight: 600,
                        "&:hover": { backgroundColor: COLORS.SECONDARY }
                      }}
                    >
                      {loadingPlan === plan._id ? <CircularProgress size={24} color="inherit" /> : `Buy Now (₹${totalPrice.toLocaleString("en-IN")})`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
        <IconButton
          onClick={() => {
            setActiveStep(0);
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          sx={{
            color: COLORS.PRIMARY,
            mr: 1,
            "&:hover": { backgroundColor: "rgba(94, 46, 145, 0.04)" }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H3,
            color: COLORS.SECONDARY,
            fontWeight: 800,
            fontSize: "28px"
          }}
        >
          FIR Request Details
        </Typography>
      </Box>

      {/* 1. Property Details */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>1. Property Details</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Property Type*</Typography>
            <TextField fullWidth sx={inputStyle} value={formData.propertyType} disabled />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Property Name</Typography>
            <TextField fullWidth placeholder="Property Name" sx={inputStyle} value={formData.propertyName} onChange={(e) => handleChange("propertyName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Building / Society Name</Typography>
            <TextField fullWidth placeholder="Building / Society Name" sx={inputStyle} value={formData.buildingName} onChange={(e) => handleChange("buildingName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Wing / Tower</Typography>
            <TextField fullWidth placeholder="Wing / Tower" sx={inputStyle} value={formData.wing} onChange={(e) => handleChange("wing", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Floor Number</Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Floor Number"
              sx={{
                ...inputStyle,
                "& input[type=number]": {
                  "-moz-appearance": "textfield"
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0
                }
              }}
              value={formData.floor}
              onKeyDown={(e) => {
                if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (Number(val) < 0) return;
                handleChange("floor", val);
              }}
            />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Unit Number / Flat Number</Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Unit Number"
              sx={{
                ...inputStyle,
                "& input[type=number]": {
                  "-moz-appearance": "textfield"
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0
                }
              }}
              value={formData.unitNumber}
              onKeyDown={(e) => {
                if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (Number(val) < 0) return;
                handleChange("unitNumber", val);
              }}
            />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Area in Sq Ft</Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Total Sq Ft (e.g. 650)"
              sx={{
                ...inputStyle,
                "& input[type=number]": {
                  "-moz-appearance": "textfield"
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0
                }
              }}
              value={formData.sqft}
              onKeyDown={(e) => {
                if (['-', '+', 'e', 'E'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (Number(val) < 0) return;
                handleChange("sqft", val);
              }}
            />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Full Property Address*</Typography>
            <TextField fullWidth multiline minRows={1} maxRows={5} placeholder="Full Property Address" sx={autoGrowStyle} value={formData.fullAddress} onChange={(e) => handleChange("fullAddress", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>State*</Typography>
            <FormControl fullWidth sx={inputStyle}>
              <Select value={formData.state || "Maharashtra"} disabled MenuProps={menuProps}>
                <MenuItem value="Maharashtra">Maharashtra</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>City*</Typography>
            <FormControl fullWidth sx={inputStyle}>
              <Select value={formData.city} disabled MenuProps={menuProps}>
                <MenuItem value="Pune">Pune</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>PIN Code*</Typography>
            <TextField fullWidth placeholder="PIN Code" sx={inputStyle} value={formData.pincode} onChange={(e) => handleChange("pincode", e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {/* 2. Owner / Client Details */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>2. Owner / Client Details</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Full Name*</Typography>
            <TextField fullWidth placeholder="Full Name" sx={inputStyle} value={formData.fullName} onChange={(e) => handleChange("fullName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Mobile Number*</Typography>
            <TextField fullWidth placeholder="Mobile Number" sx={inputStyle} value={formData.mobileNumber} onChange={(e) => handleChange("mobileNumber", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Email Address*</Typography>
            <TextField fullWidth placeholder="Email Address" sx={inputStyle} value={formData.emailAddress} onChange={(e) => handleChange("emailAddress", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Alternate Contact Number</Typography>
            <TextField fullWidth placeholder="Alternate Contact" sx={inputStyle} value={formData.altContactNumber} onChange={(e) => handleChange("altContactNumber", e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {/* 3. Inspection Details */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>3. Inspection Details</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={columnContainerStyle}>
              <Typography sx={labelStyle}>Preferred Date Range (From)*</Typography>
              <DatePicker
                format="dd/MM/yyyy"
                open={openFromDate}
                onOpen={() => setOpenFromDate(true)}
                onClose={() => setOpenFromDate(false)}
                value={formData.inspectionDateFrom}
                onChange={(newValue) => handleChange("inspectionDateFrom", newValue)}
                minDate={today}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "DD/MM/YYYY",
                    sx: datePickerInputStyle,
                    onClick: () => setOpenFromDate(true)
                  }
                }}
                sx={datePickerInputStyle}
              />
            </Box>
            <Box sx={columnContainerStyle}>
              <Typography sx={labelStyle}>Preferred Date Range (To)*</Typography>
              <DatePicker
                format="dd/MM/yyyy"
                open={openToDate}
                onOpen={() => setOpenToDate(true)}
                onClose={() => setOpenToDate(false)}
                value={formData.inspectionDateTo}
                onChange={(newValue) => handleChange("inspectionDateTo", newValue)}
                minDate={formData.inspectionDateFrom || today}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "DD/MM/YYYY",
                    sx: datePickerInputStyle,
                    onClick: () => setOpenToDate(true)
                  }
                }}
                sx={datePickerInputStyle}
              />
            </Box>

            <Box sx={columnContainerStyle}>
              <Typography sx={labelStyle}>Preferred Time (From)*</Typography>
              <TimePicker
                open={openTimeFrom}
                onOpen={() => setOpenTimeFrom(true)}
                onClose={() => setOpenTimeFrom(false)}
                value={formData.preferredTimeFrom}
                onChange={(newValue) => handleChange("preferredTimeFrom", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "hh:mm AM/PM",
                    sx: datePickerInputStyle,
                    onClick: () => setOpenTimeFrom(true)
                  }
                }}
                sx={datePickerInputStyle}
              />
            </Box>

            <Box sx={columnContainerStyle}>
              <Typography sx={labelStyle}>Preferred Time (To)*</Typography>
              <TimePicker
                open={openTimeTo}
                onOpen={() => setOpenTimeTo(true)}
                onClose={() => setOpenTimeTo(false)}
                value={formData.preferredTimeTo}
                onChange={(newValue) => handleChange("preferredTimeTo", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "hh:mm AM/PM",
                    sx: datePickerInputStyle,
                    onClick: () => setOpenTimeTo(true)
                  }
                }}
                sx={datePickerInputStyle}
              />
            </Box>
          </LocalizationProvider>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Property Status*</Typography>
            <FormControl fullWidth sx={inputStyle}>
              <Select value={formData.propertyStatus} displayEmpty MenuProps={menuProps} onChange={(e) => handleChange("propertyStatus", e.target.value)}>
                <MenuItem value="" disabled>Select Status</MenuItem>
                <MenuItem value="Under Construction">Under Construction</MenuItem>
                <MenuItem value="Ready Possession">Ready Possession</MenuItem>
                <MenuItem value="Renovation">Renovation</MenuItem>
                <MenuItem value="Occupied">Occupied</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Purpose of Inspection*</Typography>
            <FormControl fullWidth sx={inputStyle}>
              <Select value={formData.purposeOfInspection} displayEmpty MenuProps={menuProps} onChange={(e) => handleChange("purposeOfInspection", e.target.value)}>
                <MenuItem value="" disabled>Select Purpose</MenuItem>
                <MenuItem value="Before Possession">Before Possession</MenuItem>
                <MenuItem value="During Construction">During Construction</MenuItem>
                <MenuItem value="Quality Check">Quality Check</MenuItem>
                <MenuItem value="Snag Inspection">Snag Inspection</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* 4. Builder / Project Info */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>4. Builder / Project Information (Optional)</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Builder / Developer Name</Typography>
            <TextField fullWidth placeholder="Builder Name" sx={inputStyle} value={formData.builderName} onChange={(e) => handleChange("builderName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Project Name</Typography>
            <TextField fullWidth placeholder="Project Name" sx={inputStyle} value={formData.projectName} onChange={(e) => handleChange("projectName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Tower / Phase</Typography>
            <TextField fullWidth placeholder="Tower / Phase" sx={inputStyle} value={formData.towerPhase} onChange={(e) => handleChange("towerPhase", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>RERA Number</Typography>
            <TextField fullWidth placeholder="RERA Number" sx={inputStyle} value={formData.reraNumber} onChange={(e) => handleChange("reraNumber", e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {/* 5. Site Contact Person */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>5. Site Contact Person (If different)</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Contact Person Name</Typography>
            <TextField fullWidth placeholder="Contact Person Name" sx={inputStyle} value={formData.contactPersonName} onChange={(e) => handleChange("contactPersonName", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Mobile Number</Typography>
            <TextField fullWidth placeholder="Mobile Number" sx={inputStyle} value={formData.contactPersonMobile} onChange={(e) => handleChange("contactPersonMobile", e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {/* 6. Property Access */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>6. Property Access</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Is site access available?</Typography>
            <RadioGroup row value={formData.accessAvailable} onChange={(e) => handleChange("accessAvailable", e.target.value)}>
              <FormControlLabel value="true" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label={<Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>Yes</Typography>} />
              <FormControlLabel value="false" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label={<Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>No</Typography>} />
            </RadioGroup>
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Security Gate Pass Required?</Typography>
            <RadioGroup row value={formData.securityGatePass} onChange={(e) => handleChange("securityGatePass", e.target.value)}>
              <FormControlLabel value="true" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label={<Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>Yes</Typography>} />
              <FormControlLabel value="false" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label={<Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>No</Typography>} />
            </RadioGroup>
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Any Access Instructions</Typography>
            <TextField fullWidth placeholder="Instructions" sx={inputStyle} value={formData.accessInstructions} onChange={(e) => handleChange("accessInstructions", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Landmark</Typography>
            <TextField fullWidth placeholder="Landmark" sx={inputStyle} value={formData.landmark} onChange={(e) => handleChange("landmark", e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {/* 7. Additional Info */}
      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>7. Additional Information</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Special Instructions</Typography>
            <TextField fullWidth multiline minRows={1} maxRows={5} placeholder="Special Instructions" sx={autoGrowStyle} value={formData.specialInstructions} onChange={(e) => handleChange("specialInstructions", e.target.value)} />
          </Box>
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>Areas of Concern</Typography>
            <TextField fullWidth multiline minRows={1} maxRows={5} placeholder="Areas of Concern" sx={autoGrowStyle} value={formData.areasOfConcern} onChange={(e) => handleChange("areasOfConcern", e.target.value)} />
          </Box>
        </Box>

        <Typography sx={{ ...sectionHeaderStyle, fontSize: "16px", mb: 2 }}>Upload Documents (Optional)</Typography>
        <Box sx={rowContainerStyle}>
          <FileUploadBox
            label="Floor Plan (PDF)"
            file={formData.floorPlan}
            onFileSelect={(file) => handleSingleFileChange("floorPlan", file)}
          />
          <FileUploadBox
            label="Agreement Copy (PDF)"
            file={formData.agreementCopy}
            onFileSelect={(file) => handleSingleFileChange("agreementCopy", file)}
          />
          <FileUploadBox
            label="Builder Layout (PDF)"
            file={formData.builderLayout}
            onFileSelect={(file) => handleSingleFileChange("builderLayout", file)}
          />
          <FileUploadBox
            label="Previous Inspection Report (PDF)"
            file={formData.previousReport}
            onFileSelect={(file) => handleSingleFileChange("previousReport", file)}
          />
        </Box>
      </Paper>

      <Box sx={{
        display: "flex",
        justifyContent: "flex-end",
        mt: 2,
        mb: 4
      }}>
        <Button
          variant="contained"
          onClick={() => {
            if (validateForm()) {
              setActiveStep(2);
              topRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          sx={{
            backgroundColor: COLORS.SECONDARY,
            borderRadius: "10px",
            px: 5,
            py: 1.5,
            textTransform: "none",
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H3,
            "&:hover": { backgroundColor: COLORS.PRIMARY }
          }}
        >
          Review and Continue
        </Button>
      </Box>
    </Box>
  );

  const renderStep3 = () => {
    const uploadedDocsList = [
      { label: "Floor Plan", file: formData.floorPlan },
      { label: "Agreement Copy", file: formData.agreementCopy },
      { label: "Builder Layout", file: formData.builderLayout },
      { label: "Previous Inspection Report", file: formData.previousReport },
    ].filter((item) => item.file !== null && item.file !== undefined);

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setActiveStep(1);
              topRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{
              backgroundColor: "#FFFFFF",
              border: `1.5px solid ${COLORS.PRIMARY}`,
              color: COLORS.PRIMARY,
              px: 2,
              py: 0.8,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: COLORS.PRIMARY,
                color: "#FFFFFF"
              }
            }}
          >
            Back
          </Button>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>Review & Submit</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* 1. Plan & Client Info */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>1. Plan & Client Info</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Selected Plan</Typography>
                <Typography sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>{selectedPlan?.propertyType || formData.propertyType || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Property Type</Typography>
                <Typography sx={{ fontWeight: 600 }}>{formData.propertyType || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Client Name</Typography>
                <Typography sx={{ fontWeight: 600 }}>{formData.fullName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Contact Details</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.mobileNumber || "N/A"}</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '14px', color: COLORS.TEXT }}>{formData.emailAddress || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Alt Contact Number</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.altContactNumber || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 2. Property Details */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>2. Property Details</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Full Address</Typography>
                <Typography sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                  {formData.fullAddress}<br />
                  {formData.city}, {formData.state} - {formData.pincode}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Property Name</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.propertyName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Building / Society</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.buildingName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Wing / Tower</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.wing || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Floor Number</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.floor !== "" && formData.floor !== null ? formData.floor : "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Unit / Flat Number</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.unitNumber !== "" && formData.unitNumber !== null ? formData.unitNumber : "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Area in Sq Ft</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.sqft ? `${formData.sqft} sq ft` : "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 3. Inspection Details */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>3. Inspection Details</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Inspection Dates</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {formData.inspectionDateFrom ? formData.inspectionDateFrom.toLocaleDateString() : ''} to {formData.inspectionDateTo ? formData.inspectionDateTo.toLocaleDateString() : ''}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Time Slot</Typography>
                <Typography sx={{ fontWeight: 600 }}>{formData.preferredTimeSlot || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Property Status</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.propertyStatus || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Purpose</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.purposeOfInspection || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 4. Builder / Project Info */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>4. Builder / Project Information</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Builder / Developer</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.builderName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Project Name</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.projectName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Tower / Phase</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.towerPhase || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>RERA Number</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.reraNumber || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 5. Site Contact Person */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>5. Site Contact Person</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Contact Person Name</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.contactPersonName || formData.fullName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Contact Person Mobile</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.contactPersonMobile || formData.mobileNumber || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 6. Property Access */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>6. Property Access & Instructions</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Site Access Available</Typography>
                <Typography sx={{ fontWeight: 600 }}>{formData.accessAvailable === "true" ? "Yes" : "No"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Security Gate Pass Required</Typography>
                <Typography sx={{ fontWeight: 600 }}>{formData.securityGatePass === "true" ? "Yes" : "No"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Landmark</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.landmark || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Access Instructions</Typography>
                <Typography sx={{ fontWeight: 500 }}>{formData.accessInstructions || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* 7. Additional Info */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)", gridColumn: { xs: '1 / -1', md: 'span 2' } }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>7. Additional Information</Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Special Instructions</Typography>
                <Typography sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>{formData.specialInstructions || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: '12px', textTransform: 'uppercase', mb: 0.5 }}>Areas of Concern</Typography>
                <Typography sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>{formData.areasOfConcern || "N/A"}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Uploaded Documents & PDF Preview */}
          <Paper sx={{ p: 3.5, borderRadius: "12px", border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, boxShadow: "0 2px 10px rgba(0,0,0,0.02)", gridColumn: { xs: '1 / -1', md: 'span 2' } }} elevation={0}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700, mb: 2 }}>Uploaded Documents</Typography>
            <Divider sx={{ mb: 3 }} />

            {uploadedDocsList.length === 0 ? (
              <Typography sx={{ color: COLORS.MUTED_TEXT, fontStyle: 'italic' }}>No optional documents uploaded.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                {uploadedDocsList.map((doc, idx) => (
                  <Card key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: '#F9FAFB' }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PictureAsPdfIcon sx={{ color: '#D32F2F', fontSize: 24 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: COLORS.PRIMARY }}>{doc.label}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: COLORS.TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.file.name}>
                        {doc.file.name}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: COLORS.MUTED_TEXT, mt: 0.5 }}>
                        {(doc.file.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={() => handlePreviewPdf(doc.file, doc.label)}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontSize: '12px',
                          backgroundColor: COLORS.PRIMARY,
                          '&:hover': { backgroundColor: COLORS.SECONDARY }
                        }}
                      >
                        Preview PDF
                      </Button>
                      <IconButton
                        size="small"
                        title="Open in new tab"
                        onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')}
                        sx={{ border: `1px solid ${COLORS.PRIMARY}`, color: COLORS.PRIMARY, borderRadius: 1 }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setActiveStep(1);
              topRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          >
            Back
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ px: 4, py: 1.5, borderRadius: 2, backgroundColor: COLORS.PRIMARY, "&:hover": { backgroundColor: COLORS.SECONDARY } }}>
            Submit Request
          </Button>
        </Box>

        {/* PDF Preview Modal */}
        <Dialog
          open={pdfPreviewModal.open}
          onClose={handleClosePdfPreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px", height: "85vh" }
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: COLORS.PRIMARY, color: "#fff" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>{pdfPreviewModal.title}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => window.open(pdfPreviewModal.url, "_blank")}
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", border: "1px solid", textTransform: "none", fontSize: "12px" }}
              >
                Open New Tab
              </Button>
              <IconButton onClick={handleClosePdfPreview} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, flex: 1, height: "100%", overflow: "hidden" }}>
            {pdfPreviewModal.url ? (
              <iframe
                src={`${pdfPreviewModal.url}#view=FitH`}
                title={pdfPreviewModal.title}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
    );
  };

  return (
    <Box ref={topRef} sx={{ p: 3, backgroundColor: "#fafbfc", minHeight: "100%", borderRadius: "20px" }}>
      {/* Top Center Success Toaster Alert */}
      {invoiceSentEmail && (
        <Box sx={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: { xs: '90%', sm: '550px' },
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          animation: 'slideDown 0.3s ease-out',
          '@keyframes slideDown': {
            from: { transform: 'translate(-50%, -50px)', opacity: 0 },
            to: { transform: 'translate(-50%, 0)', opacity: 1 }
          }
        }}>
          <Alert
            severity="success"
            icon={false}
            sx={{
              borderRadius: '12px',
              fontSize: '0.92rem',
              py: 2,
              px: 3,
              border: '1px solid #c8e6c9',
              bgcolor: '#f4fbf7',
              position: 'relative',
            }}
          >
            {/* Close button at top right */}
            <IconButton
              onClick={() => setInvoiceSentEmail(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#2e7d32',
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#1b5e20', pr: 4 }}>
                {invoiceSentEmail.emailSent ? "🎉 Congratulations! Payment Successful" : "🎉 Plan Purchase Successful"}
              </Typography>
              <Typography variant="body2" sx={{ color: '#2e7d32', lineHeight: 1.5 }}>
                {invoiceSentEmail.emailSent ? (
                  <>
                    Your FIR plan has been activated, and the invoice has been successfully sent to <strong>{invoiceSentEmail.email}</strong>. You can now proceed with filling out the checklist request form below.
                  </>
                ) : (
                  <>
                    Your FIR plan has been activated successfully. You can now proceed with filling out the checklist request form below.
                  </>
                )}
              </Typography>
              {invoiceSentEmail.emailSent && invoiceSentEmail.purchaseId && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => window.open(`${url}/api/invoice/download/${userData._id}/${invoiceSentEmail.purchaseId}?token=${token}`)}
                    sx={{
                      bgcolor: COLORS.PRIMARY,
                      color: '#ffffff',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 3,
                      py: 0.75,
                      borderRadius: '6px',
                      '&:hover': { bgcolor: COLORS.SECONDARY || '#301352' }
                    }}
                  >
                    Download Invoice
                  </Button>
                </Box>
              )}
            </Box>
          </Alert>
        </Box>
      )}
      {activeStep > 0 && (
        <Stepper activeStep={activeStep - 1} sx={{ mb: 6 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}

      <AppToast open={toastOpen} onClose={() => setToastOpen(false)} type={toastType} title={toastTitle} message={toastMessage} />

    </Box>
  );
};

export default CreateRequestWizard;
